/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 테이블 헤더 클래스를 정의한다.
 *
 * @file /src/models/table/Header.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 10.
 */
import { Table } from '@models/table/Table';
import { Head } from '@models/table/Head';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import './Header.css';

export interface Properties {
    /**
     * 테이블
     */
    table: Table;

    /**
     * 컬럼 크기조절 여부
     */
    columnResizable?: boolean;
}

export class Header {
    /**
     * 테이블
     */
    table: Table;

    /**
     * 컬럼 크기조절 여부
     */
    columnResizable: boolean | null = null;

    /**
     * 헤더 Dom
     */
    $header: Dom = null;

    /**
     * 고정된 헤더 Dom
     */
    $freeze: Dom = null;

    /**
     * 컬럼별 Head 객체 맵
     */
    heads: Map<any, Head> = new Map();

    /**
     * 테이블헤더 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties) {
        this.table = properties.table;
        this.columnResizable = properties.columnResizable !== false;
    }

    /**
     * 테이블을 가져온다.
     *
     * @returns 테이블
     */
    getTable(): Table {
        return this.table;
    }

    /**
     * 컬럼에 해당하는 Head 객체를 가져온다. 없으면 생성한다.
     *
     * @param column - 컬럼
     * @returns Head
     */
    getHead(column: any): Head {
        if (this.heads.has(column) == false) {
            this.heads.set(column, new Head(column));
        }
        return this.heads.get(column);
    }

    /**
     * 헤더 컬럼 레이아웃 Dom을 가져온다.
     *
     * @returns 헤더 컬럼 레이아웃 Dom
     */
    $getHeader(): Dom {
        if (this.$header === null) {
            this.$header = Html.create('div', { 'data-model': 'table', 'data-role': 'header' });
        }

        return this.$header;
    }

    /**
     * 고정된 헤더 Dom을 가져온다.
     *
     * @returns 고정된 헤더 Dom
     */
    $getFreeze(): Dom {
        if (this.$freeze === null) {
            this.$freeze = Html.create('div', { 'data-model': 'table', 'data-role': 'freeze' });
            this.$freeze.setStyleProperty('anchor-name', '--aui-table-header-freeze-' + this.getTable().getId());
        }

        return this.$freeze;
    }

    /**
     * 헤더높이를 가져온다.
     *
     * @returns 헤더높이
     */
    getHeight(): number {
        return this.$getHeader().getOuterHeight();
    }

    /**
     * 헤더를 렌더링한다.
     */
    async render(): Promise<void> {
        if (this.columnResizable == true) {
            this.getTable()
                .getColumns()
                .forEach((column) => {
                    this.getHead(column).setResize();
                });
        }

        this.$getHeader().append(this.$getFreeze());
        this.updateLayout();
    }

    /**
     * freeze 상태에 맞게 컬럼 헤드 DOM을 freeze / header 영역에 배치한다.
     */
    updateLayout(): void {
        if (this.getTable().getSelection().isMultiple() == true) {
            this.$getFreeze().append(this.getTable().getSelection().$getHeader());
        }

        this.getTable().columns.forEach((column: any) => {
            const $headerDom = this.getHead(column).$getHeader();

            if (column.isHidden() == true) {
                $headerDom.addClass('hidden');
            }
            if (column.freeze == true) {
                this.$getFreeze().append($headerDom);
            } else {
                this.$getHeader().append($headerDom);
            }
        });
    }

    /**
     * 데이터스토어가 변경되었을 때 헤더를 업데이트한다.
     */
    async update(): Promise<void> {
        this.getTable()
            .getColumns()
            .forEach((column) => {
                this.getHead(column).update();
            });
    }

    /**
     * 객체를 제거한다.
     */
    destroy(): void {
        this.heads.forEach((head) => {
            head.destroy();
        });
        this.heads.clear();
        this.table = null;
        this.$header = null;
        this.$freeze = null;
    }
}
